/** ura === 1 indica usuário ChatBot */
export const isChatBotUser = (user) => Number(user?.ura) === 1;

export const ASSIGNEE_KIND_AGENT = "agent";
export const ASSIGNEE_KIND_CHATBOT = "chatbot";
export const ASSIGNEE_KIND_USER = "user";
export const ASSIGNEE_KIND_QUEUE = "queue";
export const ASSIGNEE_KIND_RECEPTIONIST = "receptionist";

export const SINGLE_SELECT_ASSIGNEE_KINDS = new Set([
  ASSIGNEE_KIND_AGENT,
  ASSIGNEE_KIND_CHATBOT,
  ASSIGNEE_KIND_QUEUE,
  ASSIGNEE_KIND_RECEPTIONIST,
]);

export const LOGGED_USERS_ONLY_FIELD = "somenteusuarioslogados";

const LEGACY_USER_KEYS = ["transferirusuario", "mudarusuario"];
const LEGACY_AGENT_KEYS = ["transferirparaagente", "mudaragente"];

export function getAgentId(agent) {
  if (!agent || typeof agent !== "object") return null;
  return agent.id ?? agent.id_agente ?? agent.agentId ?? null;
}

export function resolveUserKind(user, userId) {
  if (user) return isChatBotUser(user) ? ASSIGNEE_KIND_CHATBOT : ASSIGNEE_KIND_USER;
  return ASSIGNEE_KIND_USER;
}

export function buildAssigneeOption({ kind, id, label, raw = null }) {
  const sid = id != null ? String(id).trim() : "";
  if (!sid) return null;
  const keepStringId =
    kind === ASSIGNEE_KIND_AGENT ||
    kind === ASSIGNEE_KIND_QUEUE ||
    kind === ASSIGNEE_KIND_RECEPTIONIST;
  const resolvedId = keepStringId ? sid : Number(id);
  if (!keepStringId && (!Number.isFinite(resolvedId) || resolvedId <= 0)) return null;
  return {
    kind,
    id: resolvedId,
    label: String(label || sid),
    raw,
  };
}

/** Converte valores legados (transferirusuario / transferirparaagente) em opções do select unificado. */
export function legacyValuesToAssigneeSelection(values = {}, { users = [], agents = [] } = {}) {
  const agentRaw = LEGACY_AGENT_KEYS.map((k) => values?.[k]).find(
    (v) => v != null && String(v).trim() !== ""
  );
  if (agentRaw != null && String(agentRaw).trim() !== "") {
    const agentId = String(agentRaw).trim();
    const agent = (agents || []).find((a) => String(getAgentId(a)) === agentId);
    const label = agent?.name ?? agent?.nome ?? values?.labels?.transferirparaagente ?? `Agente`;
    return [buildAssigneeOption({ kind: ASSIGNEE_KIND_AGENT, id: agentId, label, raw: agent })].filter(Boolean);
  }

  const userRaw = LEGACY_USER_KEYS.map((k) => values?.[k]).find(
    (v) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0)
  );
  if (userRaw == null || userRaw === "") return [];

  const ids = Array.isArray(userRaw) ? userRaw : [userRaw];
  const out = [];
  for (const id of ids) {
    if (id == null || id === "" || id === "default") continue;
    const numId = Number(id);
    if (!Number.isFinite(numId) || numId <= 0) continue;
    const user = (users || []).find((u) => Number(u.id) === numId);
    const kind = resolveUserKind(user, numId);
    const label =
      user?.nome ??
      user?.name ??
      (Array.isArray(values?.labels?.transferirusuario)
        ? values.labels.transferirusuario[ids.indexOf(id)]
        : values?.labels?.transferirusuario) ??
      `Usuário #${numId}`;
    const opt = buildAssigneeOption({ kind, id: numId, label, raw: user });
    if (opt) out.push(opt);
  }
  return out;
}

/** Persiste no formato legado consumido pela API de automação. */
export function assigneeSelectionToLegacyValues(selection = []) {
  const list = Array.isArray(selection) ? selection.filter(Boolean) : [];
  if (!list.length) {
    return {
      transferirusuario: "",
      transferirparaagente: "",
      mudarusuario: "",
      mudaragente: "",
    };
  }

  if (list.length === 1 && list[0].kind === ASSIGNEE_KIND_AGENT) {
    const agentId = String(list[0].id);
    return {
      transferirusuario: "",
      transferirparaagente: agentId,
      mudarusuario: "",
      mudaragente: agentId,
    };
  }

  const ids = list
    .filter((item) => item.kind === ASSIGNEE_KIND_USER || item.kind === ASSIGNEE_KIND_CHATBOT)
    .map((item) => Number(item.id))
    .filter((id) => Number.isFinite(id) && id > 0);

  const transferirusuario = ids.length <= 1 ? ids[0] ?? "" : ids;

  return {
    transferirusuario,
    transferirparaagente: "",
    mudarusuario: transferirusuario,
    mudaragente: "",
  };
}

export function assigneeSelectionLabels(selection = []) {
  const list = Array.isArray(selection) ? selection : [];
  if (!list.length) return "";
  return list.map((item) => item.label).filter(Boolean).join(", ");
}

/** Humanos permitem multi-select; demais destinos apenas um. */
export function selectionAllowsMultiple(selection = []) {
  const list = Array.isArray(selection) ? selection : [];
  if (!list.length) return true;
  if (list.some((item) => SINGLE_SELECT_ASSIGNEE_KINDS.has(item.kind))) return false;
  return list.every((item) => item.kind === ASSIGNEE_KIND_USER);
}

export function selectionRequiresLoggedUsersOnlyDisabled(selection = []) {
  const list = Array.isArray(selection) ? selection : [];
  return list.some(
    (item) => item.kind === ASSIGNEE_KIND_AGENT || item.kind === ASSIGNEE_KIND_CHATBOT
  );
}

export function normalizeAssigneeSelectionOnChange(prevSelection = [], nextSelection = []) {
  const next = Array.isArray(nextSelection) ? nextSelection.filter(Boolean) : [];
  if (!next.length) return [];

  const last = next[next.length - 1];
  if (SINGLE_SELECT_ASSIGNEE_KINDS.has(last.kind)) {
    return [last];
  }

  const prevHadExclusive = (prevSelection || []).some((item) =>
    SINGLE_SELECT_ASSIGNEE_KINDS.has(item.kind)
  );
  if (prevHadExclusive) {
    return next.filter((item) => item.kind === ASSIGNEE_KIND_USER);
  }

  return next.filter(
    (item, idx, arr) =>
      item.kind === ASSIGNEE_KIND_USER &&
      arr.findIndex((x) => x.kind === ASSIGNEE_KIND_USER && Number(x.id) === Number(item.id)) === idx
  );
}

export function buildAssigneeTransferOptions({
  users = [],
  agents = [],
  excludeAgentIds = [],
  includeQueue = false,
  includeReceptionist = false,
  queueLabel = "Fila de atendimento humano",
  receptionistLabel = "Recepcionista (agente de IA)",
  shortcutsGroup = "Fila e atalhos",
} = {}) {
  const exclude = new Set((excludeAgentIds || []).map((id) => String(id)));
  const humanUsers = (users || []).filter((u) => !isChatBotUser(u));
  const chatbotUsers = (users || []).filter(isChatBotUser);

  const options = [];

  if (includeQueue) {
    const queueOpt = buildAssigneeOption({
      kind: ASSIGNEE_KIND_QUEUE,
      id: "fila_humano",
      label: queueLabel,
    });
    if (queueOpt) options.push({ ...queueOpt, group: shortcutsGroup });
  }

  if (includeReceptionist) {
    const recOpt = buildAssigneeOption({
      kind: ASSIGNEE_KIND_RECEPTIONIST,
      id: "receptionist",
      label: receptionistLabel,
    });
    if (recOpt) options.push({ ...recOpt, group: shortcutsGroup });
  }

  (agents || []).forEach((agent) => {
    const id = getAgentId(agent);
    if (id == null || exclude.has(String(id))) return;
    const opt = buildAssigneeOption({
      kind: ASSIGNEE_KIND_AGENT,
      id,
      label: agent.name ?? agent.nome ?? `Agente #${id}`,
      raw: agent,
    });
    if (opt) options.push({ ...opt, group: "Agentes de IA" });
  });

  humanUsers.forEach((user) => {
    const opt = buildAssigneeOption({
      kind: ASSIGNEE_KIND_USER,
      id: user.id,
      label: user.nome ?? user.name ?? `Usuário #${user.id}`,
      raw: user,
    });
    if (opt) options.push({ ...opt, group: "Usuários" });
  });

  chatbotUsers.forEach((user) => {
    const name = user.nome ?? user.name ?? `ChatBot #${user.id}`;
    const opt = buildAssigneeOption({
      kind: ASSIGNEE_KIND_CHATBOT,
      id: user.id,
      label: name.endsWith("(ChatBot)") ? name : `${name} (ChatBot)`,
      raw: user,
    });
    if (opt) options.push({ ...opt, group: "ChatBots" });
  });

  return options;
}
